import * as S from './styles';
import { Modal } from '@/components';
import { useAlert, useGlobals, useNetworkStatusContext, useTables } from '@/hooks';
import { setOnStorage, getOnStorage } from '@/shared';
import { useDownloadFiles, useEvents } from '@/talons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';
import { invalidateCatalogCachesAfterSync } from '@/shared/catalogCacheInvalidate';
import { useDbFunctions } from '@/talons/dbFunctions/useDBFunctions';

interface Props {
  children?: any;
  cbAction?: () => void;
  isLoading?: boolean | null;
}

const ModalSync = ({ children, isLoading, cbAction }: Props) => {
  const { tables } = useTables();
  const db = useDbFunctions();
  const {showAlert} = useAlert();
  const { connectivityStatus } = useNetworkStatusContext();
  const reloadTables = useDbFunctions().reloadTables;
  const { registerEvent } = useEvents(db);
  const { setLastSync, getDate, downloadingFileName: fileName, downloadSpeedBps, downloadingFiles, startedDownloadingFiles, fileDownload, fileDownloadProgress, progressPercent } = useGlobals();

  const curLoading = useRef(false);
  const enableCloseRef = useRef(false);

  const {
    loading,
    preparing,
    hasError,
    noUpdate,
    isSuccess,
    isOpenedModal,

    setError,
    loadFiles,
    getLinks,
    openModalSync,
  } = useDownloadFiles();

  const handleLoadFiles = useCallback(async () => {    
    if (connectivityStatus === 'offline') {
      showAlert({
        bgType: 'secondary',
        bordered: true,
        theme: 'warning',
        close: 'icon',
        title: 'Você está offline',
        borderLeft: true,
        showIcon: true,
        time: 5000,
        message: 'Por favor verifique sua conexão com a internet e tente novamente.',
      });
      return;
    }

    openModalSync(true);
    setError(false);
    if (window.location.href.includes('login')) return;

    // Não bloqueia getdataset/sync na fila HTTP nem segura o modal até timeout do api-proxy de eventos.
    queueMicrotask(() => {
      void registerEvent({
        tipoEvento: 'SINC_DATASETS',
        obsEvento: 'Sincronização de datasets iniciada',
      }).catch(() => {});
    });

    let data = undefined;
    if (isLoading) data = await loadFiles();
    else data = await getLinks();

    const formattedDate = getDate();
    setLastSync(formattedDate);
    if (cbAction) cbAction();

    if (!getOnStorage('datasets')) {
      const datasets = getOnStorage('new_data_sets');
      setOnStorage('datasets', datasets || []);
    }

    if (data?.cliente) tables.current.cliente = data?.cliente;

    // Recarrega todas as tabelas do IndexedDB para manter o estado em memória consistente
    try {
      await reloadTables();
      invalidateCatalogCachesAfterSync();
    } catch (err) {
      console.error('ModalSync: falha ao recarregar tabelas após sync', err);
    }
  }, [connectivityStatus, showAlert]);

  useEffect(() => {
    const runSigned = async () => {
      if (!isLoading || curLoading.current) return;
      curLoading.current = true;
      try {
        await reloadTables();
        invalidateCatalogCachesAfterSync();
        if (cbAction) cbAction();
      } catch (err) {
        console.error('ModalSync: falha ao recarregar tabelas após sync', err);
      }
    }
    const runUnsigned = async () => {
      if (!isLoading || curLoading.current) return;

      curLoading.current = true;
      handleLoadFiles();
    }

    if (window.location.href.includes('login')) {
      runUnsigned();
    } else {
      runSigned();
    }
  }, [isLoading, cbAction]);

  const currentLoading = useMemo(() => {
    return isLoading || loading || preparing;
  }, [loading, isLoading, preparing]);

  const handleReloadApp = async () => {
    await unregisterServiceWorker({});
    window.location.reload();
  }

  const handleModal = (value: boolean) => {
    if (!enableCloseRef.current) return;
    openModalSync(false);
    if (!value) handleReloadApp();
  }

  useEffect(() => {
    setTimeout(() => {
      enableCloseRef.current = true;
    }, 500);
  }, []);

  const lastSpeedBps = useRef(0);

  const speedLabel = useMemo(() => {
    const value = downloadSpeedBps || lastSpeedBps.current;
    if (downloadSpeedBps !== 0) lastSpeedBps.current = downloadSpeedBps;
    const kbps = (value * 8) / 1000;
    if (kbps >= 1000) {
      const mbps = (kbps / 1000).toFixed(1).replace(/\.0$/, '');
      return `${mbps}Mbps`;
    }
    return `${Math.round(kbps)}kbps`;
  }, [downloadSpeedBps]);

  return (
    <>
      <S.ButtonSpan onClick={handleLoadFiles} data-test="modal-sync-button-open">
        {children}
      </S.ButtonSpan>

      <Modal id="modal-sync" isOpen={isOpenedModal} isLoading={currentLoading} closeModal={handleModal}>
        <S.Container>
          {currentLoading && (
            <S.BigLoadContent>
              <S.BigLoad>
                <S.CentralLoad />
              </S.BigLoad>
            </S.BigLoadContent>
          )}

          {hasError && !currentLoading ? <S.Error size={76} /> : isSuccess && <S.Success size={76} />}

          <S.LoadingMsg>
            {hasError && !currentLoading ? 'Falha na Sincronização' : isSuccess ? 'Concluído' : 'Aguarde'}
            ...
          </S.LoadingMsg>

          <S.Loader>
            <S.Message>
              {hasError && !currentLoading
                ? 'Falha na sincronização dos dados, por favor tente novamente!'
                : noUpdate
                  ? 'Não há atualizações disponíveis.'
                  : isSuccess
                    ? 'Sua sincronização já está concluída. Você já pode utilizar o app normalmente.'
                    : 'Estamos sincronizando seu aplicativo, logo você poderá navegar normalmente.'}
            </S.Message>
          </S.Loader>

          {/* {!hasError && !noUpdate && ( */}
          <S.ProgressContent $collapsed={false}>
            <S.ProgressBar $progress={progressPercent}>
              <S.ProgressStyle id="progress-bar" style={{ width: `${progressPercent}%` }} />
              <S.ProgressText>
                <S.ProgressTextLayer $overProgress={false}>
                  <span>{fileDownloadProgress}</span>
                  <span>{fileDownload}</span>
                </S.ProgressTextLayer>
                <S.ProgressTextLayer $overProgress={true}>
                  <span>{fileDownloadProgress}</span>
                  <span>{fileDownload}</span>
                </S.ProgressTextLayer>
              </S.ProgressText>
            </S.ProgressBar>
          </S.ProgressContent>
          {/* )} */}

          {currentLoading && (
            <>
              <S.Row>
                <S.InternetSpeed>{speedLabel}</S.InternetSpeed>
                <S.FileLoading>Sincronizando  {fileName}</S.FileLoading>
                <S.FilesNumber>{Math.min(startedDownloadingFiles.length, downloadingFiles.length)}/{downloadingFiles.length}</S.FilesNumber>
              </S.Row>

              <S.LittleLoading>
                <S.LittleLoadingPulse />
              </S.LittleLoading>
            </>
          )}

          {(isSuccess || (hasError && !currentLoading)) && (
            <>
              <S.SuccessText>{hasError ? '' : 'Sincronização concluída!'}</S.SuccessText>

              <S.ButtonClose data-test="modal-sync-close-button" onClick={() => handleModal(false)}>
                Fechar
              </S.ButtonClose>
            </>
          )}
        </S.Container>
      </Modal>
    </>
  );
};

export default ModalSync;
